<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    app
    temporary
    width="420"
    class="d-print-none"
    data-cy="nav"
  >
    <!-- the drawer has no old menu button, so it keeps the new menu switch -->
    <div class="px-4 py-3">
      <slot name="account" :parts="['who', 'menuSwitch', 'logout']"/>
    </div>
    <div v-if="!fewPages" class="px-3 pb-3">
      <v-text-field
        v-model="search"
        ref="search"
        dense
        outlined
        clearable
        hide-details
        autofocus
        prepend-inner-icon="mdi-magnify"
        :placeholder="placeholder"
        data-cy="nav-search"
        @keydown.enter="openFirstMatch"
      />
    </div>
    <v-divider/>

    <nav-customer-choice
      :email="emailSearch"
      :customers="customerChoices"
      @pick="openCustomerPage"
      @close="customerChoices = []"
    />

    <v-list v-if="search" dense data-cy="nav-results">
      <v-list-item v-if="emailSearch" disabled>
        <v-list-item-content>Enter opens the customer "{{ emailSearch }}"</v-list-item-content>
      </v-list-item>
      <v-list-item v-else-if="matches.length === 0" disabled>
        <v-list-item-content>no pages match "{{ search }}"</v-list-item-content>
      </v-list-item>
      <nav-item
        v-for="match of matches"
        :key="match.path.join('/')"
        :item="match.item"
        :trail="match.path.slice(0, -1).join(' › ')"
        :location="location"
      />
    </v-list>

    <v-list v-else dense>
      <v-list-group
        v-for="group of shortcutGroups"
        :key="group.title"
        :value="true"
        :prepend-icon="group.icon"
        :data-cy="`nav-section-${group.cy}`"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">{{ group.title }}</v-list-item-title>
            <v-list-item-subtitle class="nav-section-summary">{{ group.summary }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <nav-item
          v-for="page of group.pages"
          :key="`${page.item.name}${JSON.stringify(page.page.params)}`"
          :item="page.item"
          :to="page.to"
          :location="location"
        />
      </v-list-group>
      <v-list-group
        v-for="section of sections"
        :key="section.title"
        :value="isShort"
        :prepend-icon="section.icon"
        :data-cy="`nav-section-${section.title}`"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">{{ section.title }}</v-list-item-title>
            <v-list-item-subtitle class="nav-section-summary">{{ section.summary }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <nav-item
          v-for="item of section.items"
          :key="item.name"
          :item="item"
          :location="location"
          :depth="0"
        />
      </v-list-group>
    </v-list>

    <template v-slot:append>
      <div class="px-4 py-1">
        <slot name="account" :parts="['apiSwitch']"/>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import NavCustomerChoice from '@/components/NavCustomerChoice.vue';
import NavItem from '@/components/NavItem.vue';
import navSearch from '@/components/nav-search';
import {hasFewPages, isShortMenu} from '@/nav-helpers';

/**
 * The menu on a narrow screen (NavColumns on a wide one): the pages grouped by area, each with
 * a one line summary, and pages only reached from another page (a customer, a component)
 * nested under that page.
 */
export default {
  name: 'NavDrawer',
  components: {NavCustomerChoice, NavItem},
  mixins: [navSearch],
  props: {
    value: {type: Boolean, required: true},
    sections: {type: Array, required: true},
    /** starred and recently visited pages: [{item, to, page}] */
    starredPages: {type: Array, required: true},
    recentPages: {type: Array, required: true},
    /** turns a nav item into a router location (adds the current dates/production) */
    location: {type: Function, required: true},
  },
  computed: {
    /** a role with only a few pages has nothing to search */
    fewPages() {
      return hasFewPages(this.sections);
    },
    /** a role with a few pages sees them all at once */
    isShort() {
      return isShortMenu(this.sections);
    },
    /** open first, above the sections; pages are starred on a wide screen */
    shortcutGroups() {
      return [
        {title: 'Starred', cy: 'starred', icon: 'mdi-star-outline', summary: 'Pages you starred', pages: this.starredPages},
        {title: 'Recently used', cy: 'recent', icon: 'mdi-history', summary: 'The last pages you opened', pages: this.recentPages},
      ].filter(group => group.pages.length);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.search = '';
      }
    },
  },
};
</script>

<style scoped>
.nav-section-summary {
  white-space: normal;
}
</style>
